import { math } from '@/utils/business/utilsMath';
import { getStorageSync, setStorageSync } from '@/utils/storage.js';
import { getReferencePrice, isCooperationPrice } from '@/utils/productPrice';
import type { UserInfo as PriceUserInfo } from '../../shared/type/UserInfo';

// Type definitions
interface PriceSegment {
	integer: string;
	decimal: string | number;
}

interface UrlParams {
	[key: string]: string;
}

interface ProductItem {
	productId?: string;
	productid?: string;
	companyId?: string;
	referencePrice?: number;
	referenceprice?: number;
	issee?: number;
	iscontrolsales?: number;
	isControlSales?: number;
}

// Navigation functions adapted for Nuxt/Vue3
export const navigateBack = (params: number | unknown): void => {
	setTimeout(() => {
		if (import.meta.client) {
			if (typeof params === 'number') {
				window.history.go(-params);
			} else {
				window.history.back();
			}
		}
	}, 100);
};

// Cache previous step (adapted for web)
export const navBack = (): void => {
	// Web version - store in sessionStorage instead of uniapp storage
	if (import.meta.client) {
		const currentPath = window.location.pathname + window.location.search;
		sessionStorage.setItem('previousRoute', currentPath);
	}
};

/**
 * 价格展示格式化。
 *
 * **参数顺序**：`productPrice` → **`userInfo`（必传，可为 `null` 表示未登录）** → **`fixNum`（可选，默认 2）** → **`isControlSales`（可选）** → **`isSeeNoBuy`（可选）**。
 *
 * **控销**（`isControlSales === 1`）：
 * - **`isSeeNoBuy === 0`**：白名单，走**数值格式化**展示价格。
 * - **`isSeeNoBuy === 1`**：本函数返回「可询价」（与详情侧「可见不可买」一致）。**`isSee`**（可见 / 后端是否过滤商品）由**接口与详情页**处理，不在此函数入参。
 *
 * **`isControlSales` 非 1 或未传**：不走控销挡价，直接按数字格式化（仍受上文登录/认证限制）。
 *
 * `userInfo` 请由调用方传入（如 `usePriceFormat()` / `useUserInfo()`），勿在本函数内读 `localStorage`。
 */
export const priceFormat = (
	productPrice: string | number,
	userInfo: PriceUserInfo | null,
	fixNum?: number,
	isControlSales?: number | string,
	isSeeNoBuy?: number | string
): string | number => {
	const decimals = fixNum !== undefined && fixNum !== null && !Number.isNaN(Number(fixNum)) && Number(fixNum) >= 0 ? Number(fixNum) : 2;

	if (!userInfo) return '登录可见';

	// merchantStatus说明：0-注册后待认证, 1-认证中, 2-一审通过待二审, 3-审核通过, 4-审核拒绝
	const isMerchant = [0, 1, 2, 4].includes(userInfo?.merchantStatus ?? -1);
	if (isMerchant) return '认证可见';

	if (productPrice === '合作价') return '合作价';
	if (productPrice === '可询价' || productPrice == 9999999.99) return '可询价';
	if (productPrice == 0) return 0.0;
	if (!productPrice) return '可询价';

	const isControlSalesMissing =
		isControlSales === undefined || isControlSales === null || (typeof isControlSales === 'string' && isControlSales === '');
	const isControlSalesNum = isControlSalesMissing ? NaN : Number(isControlSales);
	if (!Number.isNaN(isControlSalesNum) && isControlSalesNum === 1) {
		if (Number(isSeeNoBuy) === 1) {
			return '可询价';
		}
		// isSeeNoBuy===0 等「非 1」：白名单，向下执行数字格式化
	}

	let price = productPrice + '';
	if (price.lastIndexOf('.') != -1) {
		const decimal = price.substring(price.lastIndexOf('.') + 1);
		productPrice = parseFloat(productPrice as string);
		if (decimal.length >= 3) {
			return Math.round(productPrice * 10000) / 10000;
		}
	}
	let res = Math.round((productPrice as number) * 100) / 100;
	return res.toFixed(decimals);
};

/** 与 ShowAmount 一致：须为对象，禁止返回字符串（否则 integer/decimal 为 undefined 会渲染成「￥.」） */
export const getPriceSegment = (
	productPrice,
	userInfo: PriceUserInfo | null
): PriceSegment & { textOnly?: boolean } => {
	const newPrice = priceFormat(productPrice, userInfo);

	// 处理各种特殊价格文本（整段展示，不加「￥」前缀）
	if (newPrice === '可询价' || newPrice === '登录可见' || newPrice === '认证可见' || newPrice === '合作价') {
		return { integer: String(newPrice), decimal: '', textOnly: true };
	}

	// 处理数字价格
	let strPrice = newPrice + '';
	let arrPrice = strPrice.split('.');

	if (arrPrice.length == 1) {
		return {
			integer: arrPrice[0],
			decimal: '00',
		};
	} else if (arrPrice.length == 2) {
		return {
			integer: arrPrice[0],
			decimal: arrPrice[1].padEnd(2, '0').substring(0, 2), // 确保2位小数
		};
	} else {
		return { integer: '可询价', decimal: '', textOnly: true };
	}
};

import { mobileProductDetailPath } from '@/utils/link/mobileProductDetail';

// Navigate to product detail (adapted for web routing)
export const toProductDitall = (item: ProductItem): void => {
	const productId = item.productId || item.productid;
	const companyId = item.companyId;
	if (productId && companyId) {
		const path = mobileProductDetailPath(companyId, productId);
		if (path) navigateTo(path);
	}
};

// Check if product requires quotation
export const isRFQ = (item: ProductItem): boolean => {
	// Check if user is logged in
	const isLogin = import.meta.client && (getStorageSync('user_info') || sessionStorage.getItem('access_token'));
	const referencePrice = getReferencePrice(item);

	if (isLogin) {
		if (isCooperationPrice(item)) {
			return false;
		}
		if (item.issee === 1 && (item.iscontrolsales === 1 || item.isControlSales === 1)) {
			return true;
		} else {
			if (referencePrice === 9999999.99 || (referencePrice !== 0 && referencePrice !== '0' && !referencePrice)) {
				return true;
			} else {
				return false;
			}
		}
	} else {
		return true;
	}
};

// Compress picture URL
export const compressPic = (url: string, percent?: number): string => {
	percent = percent || 50;
	return url + `?x-oss-process=image/resize,p_${percent}`;
};

// To fixed number function
export const toFixedNum = (data: number, n: number): string => {
	let numbers = '';
	// Add n zeros after decimal places
	for (let i = 0; i < n; i++) {
		numbers += '0';
	}
	const s = 1 + numbers;
	// If integer, add trailing zeros
	const spot = '.' + numbers;
	// Math.round rounds half up parseFloat() parses a string and returns a floating point number
	const value = math.divide(Math.round(math.multiply(data, s)), s);
	// Split from decimal point
	const d = value.toString().split('.');

	if (d.length == 1) {
		return value.toString() + spot;
	}

	if (d.length > 1) {
		if (d[1].length < 2) {
			return value.toString() + '0';
		}
		return value.toString();
	}

	return value.toString();
};

// Get persistent device ID using fingerprinting
export const getPersistentDeviceID = async (): Promise<string> => {
	// Try to get deviceId from localStorage
	// Return empty string in SSR environment
	if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
		return '';
	}

	let deviceId = getStorageSync('deviceId') as string | null;

	// If not found, generate new deviceId
	if (!deviceId) {
		try {
			const FingerprintJS = (await import('@fingerprintjs/fingerprintjs')).default;
			const fp = await FingerprintJS.load();
			const result = await fp.get();
			deviceId = result.visitorId;
		} catch (error) {
			console.warn('Failed to generate fingerprint:', error);
			// Generate fallback ID
			deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
		}
	}

	// Ensure deviceId is stored in localStorage
	setStorageSync('deviceId', deviceId);

	return deviceId;
};

// Additional utility functions from the original file that might be useful:

// Get URL parameters
export const getUrlParams = (url?: string): UrlParams => {
	const params: UrlParams = {};
	const parser = document.createElement('a');
	parser.href = url || window.location.href;
	const query = parser.search.substring(1);
	const vars = query.split('&');

	for (let i = 0; i < vars.length; i++) {
		const pair = vars[i].split('=');
		params[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
	}

	return params;
};

// Generate UUID
export const generateUUID = (): string => {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
		const r = (Math.random() * 16) | 0;
		const v = c == 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};

// Format phone number
export const formatPhone = (phone?: string): string => {
	if (!phone) return '';
	const cleaned = phone.replace(/\D/g, '');
	const match = cleaned.match(/^(\d{3})(\d{4})(\d{4})$/);
	if (match) {
		return `${match[1]}-${match[2]}-${match[3]}`;
	}
	return phone;
};

// Validate email
export const validateEmail = (email: string): boolean => {
	const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return re.test(email);
};

// Format currency
export const formatCurrency = (amount: number, currency = 'CNY'): string => {
	if (typeof amount !== 'number') return amount as unknown as string;
	return new Intl.NumberFormat('zh-CN', {
		style: 'currency',
		currency: currency,
		minimumFractionDigits: 2,
	}).format(amount);
};

export function toNumber(val: unknown): number | null {
	if (val === undefined || val === null || val === '') return null;
	const n = Number(val);
	return Number.isFinite(n) ? n : null;
}
